<script setup lang="ts">
import {
    CalendarDays,
    ChartNoAxesColumn,
    CirclePlay,
    Sparkles,
    Wand,
} from '@lucide/vue';
import DashboardMockup from '@/components/marketing/shared/DashboardMockup.vue';
import EarlyAccessForm from '@/components/marketing/shared/EarlyAccessForm.vue';

const callouts = [
    {
        label: 'Generate content with AI',
        icon: Wand,
        position: 'top-full right-4 -translate-y-1/2 lg:right-8',
    },
    {
        label: 'Plan and schedule your content',
        icon: CalendarDays,
        position: 'bottom-0 left-0 -translate-x-4 translate-y-1/3',
    },
    {
        label: 'Publish and measure',
        icon: ChartNoAxesColumn,
        position: 'bottom-0 right-0 -translate-y-1/3 translate-x-4',
    },
];
</script>

<template>
    <section class="relative overflow-hidden pt-14 pb-20 sm:pt-20">
        <div
            class="from-primary/10 via-secondary/5 pointer-events-none absolute inset-x-0 top-0 -z-10 h-[520px] bg-gradient-to-b to-transparent"
            aria-hidden="true"
        />

        <div
            class="mx-auto grid max-w-7xl items-center gap-14 px-4 sm:px-6 lg:grid-cols-[1fr_1.05fr] lg:px-8"
        >
            <div class="flex flex-col items-start gap-6">
                <span
                    class="border-primary/25 bg-primary/10 inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-medium"
                >
                    <Sparkles
                        class="text-primary size-3.5"
                        aria-hidden="true"
                    />
                    AI-powered marketing assistant
                </span>

                <h1
                    class="font-display text-[2.75rem] leading-[1.05] text-balance sm:text-5xl md:text-6xl"
                >
                    Your marketing,
                    <span class="text-primary">on autopilot.</span>
                </h1>

                <p
                    class="text-muted-foreground max-w-xl text-lg leading-relaxed"
                >
                    LaunchStation learns your business, creates content, and
                    helps you publish across your channels from one simple
                    workspace.
                </p>

                <EarlyAccessForm
                    variant="hero"
                    id-prefix="hero"
                    class="max-w-xl"
                />

                <a
                    href="#how-it-works"
                    class="text-foreground hover:bg-accent -ml-3 inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium"
                >
                    <CirclePlay class="size-4" aria-hidden="true" />
                    See how it works
                </a>
            </div>

            <div class="relative">
                <DashboardMockup />

                <span
                    v-for="callout in callouts"
                    :key="callout.label"
                    :class="[
                        'border-border bg-card absolute hidden items-center gap-2 rounded-xl border px-3 py-2 text-sm shadow-sm lg:flex',
                        callout.position,
                    ]"
                >
                    <component
                        :is="callout.icon"
                        class="text-primary size-4"
                        aria-hidden="true"
                    />
                    {{ callout.label }}
                </span>

                <p class="text-muted-foreground mt-4 text-center text-xs">
                    Illustrative product preview.
                </p>
            </div>
        </div>
    </section>
</template>
